import { useState } from 'react';

import { useQuery } from '@apollo/client';
import { css } from '@emotion/react';
import { ExpandMore, ViewList, AccountTree } from '@mui/icons-material';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Stack,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
} from '@mui/material';
import { EliminationCupMode, Ranking } from '@scorenco/components';
import { Types, useTranslation, SportType } from '@scorenco/core';

import { Link, Game } from '../../../../components';
import { T_KEYS } from '../../../../translations';
import { LiveMatchsContainer } from '../LiveMatchsContainer';
import { Tournament_GetChallengeDocument } from '../Tournament.query.generated';

const getStatus = (status: string) => {
  switch (status) {
    case 'to_come':
      return null;
    case 'live':
      return 'En cours';
    case 'finish':
      return 'Terminé';
    default:
      return null;
  }
};

type ChallengeProps = {
  challenge: Types.Tournaments_Challenge;
  tournament: any;
};

export const Challenge = ({
  challenge: challengeTmp,
  tournament,
}: ChallengeProps) => {
  const { t } = useTranslation();
  const [phaseMode, setPhaseMode] = useState(true);

  const challengeQuery = useQuery(Tournament_GetChallengeDocument, {
    variables: {
      challengeId: challengeTmp.id,
    },
    context: {
      role: 'anonymous',
    },
    skip: !challengeTmp.id,
  });

  const challenge = challengeQuery.data?.tournaments_challenge_by_pk;

  if (!challenge) {
    return null;
  }

  let EliminationCupModeHeight;

  return (
    <>
      <LiveMatchsContainer challengeId={challenge.id} />

      {challenge.rules.map((rule, idxRule) =>
        rule.phases.map((phase, idxPhase) => {
          const ranking =
            phase.engagements?.length > 2 &&
            // (rule.rule_type === 1 ||
            //   rule.rule_type === 5 ||
            //   rule.rule_type === 6) &&
            phase.engagements?.[0]?.ranking?.rank &&
            phase.engagements
              .slice()
              .sort((e1, e2) => e1.ranking?.rank - e2.ranking?.rank);
          const status = getStatus(phase.status);

          return (
            <Accordion
              id={phase.name.replaceAll(' ', '')}
              key={`${idxRule}${idxPhase}`}
              square
              sx={{
                p: 0,
                borderTop: idxRule + idxPhase === 0 ? '1px solid' : undefined,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{ width: '100%' }}
              >
                <Stack sx={{ gap: 1, py: 1 }}>
                  <Stack
                    sx={{
                      textTransform: 'uppercase',
                      alignItems: 'center',
                      gap: 1,
                      fontWeight: 'bold',
                    }}
                    direction="row"
                  >
                    <Box>{phase.name}</Box>
                    {status && (
                      <Box
                        display="inline"
                        sx={{
                          padding: ' 0.25rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.8rem',
                          color: 'primary.contrastText',
                          backgroundColor: 'primary.main',
                        }}
                      >
                        <Box
                          css={css`
                            @keyframes fade-in {
                              0% {
                                opacity: 100%;
                              }

                              50% {
                                opacity: 50%;
                              }

                              100% {
                                opacity: 100%;
                              }
                            }
                            animation: ${status === 'En cours'
                              ? 'fade-in 1.5s linear infinite'
                              : ''};
                          `}
                        >
                          {status}
                        </Box>
                      </Box>
                    )}
                  </Stack>

                  {tournament?.sport_id === SportType.Volley ? (
                    <span
                      css={css`
                        font-weight: 500;
                        font-size: 1rem;
                      `}
                    >
                      Matchs de {rule.period_nb} sets gagnants
                    </span>
                  ) : (
                    rule.period_duration && (
                      <span
                        css={css`
                          font-weight: 500;
                          font-size: 1rem;
                        `}
                      >
                        (
                        {rule.period_nb === 1
                          ? `Matchs de ${rule.period_duration} minutes`
                          : `Mi-temps de ${rule.period_duration} minutes`}
                        )
                      </span>
                    )
                  )}
                </Stack>
              </AccordionSummary>
              {function () {
                if (phase?.games?.length === 18) {
                  EliminationCupModeHeight = css`
                    height: 105rem;
                  `;
                } else if (phase?.games?.length === 12) {
                  EliminationCupModeHeight = css`
                    height: 68rem;
                  `;
                } else if (phase?.games?.length === 4) {
                  EliminationCupModeHeight = css`
                    height: 40rem;
                  `;
                }
              }.call(this)}
              <AccordionDetails
                css={phaseMode ? EliminationCupModeHeight : undefined}
                sx={{
                  width: 1,
                  padding: '8px 16px 16px',
                }}
              >
                <Ranking ranking={ranking} tournament={tournament} />

                {ranking === undefined ? (
                  <>
                    <Box
                      sx={{
                        pb: '1rem',
                      }}
                    >
                      <Box
                        sx={{
                          width: 1,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-end',
                          p: '0 16px 16px 16px',
                        }}
                      >
                        <Typography
                          sx={{ fontWeight: 600, textTransform: 'uppercase' }}
                        >
                          {phaseMode
                            ? 'Vue en arbre (les matchs de classement ne sont pas affichés)'
                            : 'Vue en liste'}
                        </Typography>
                        <ToggleButtonGroup
                          value={phaseMode}
                          exclusive
                          onClick={() =>
                            setPhaseMode((phaseMode) => !phaseMode)
                          }
                          aria-label="Choisissez une vue"
                        >
                          <ToggleButton
                            value={true}
                            aria-label="Vue en arbre"
                            sx={{
                              color: (theme) => theme.vars.palette.primary.main,
                            }}
                          >
                            <AccountTree />
                          </ToggleButton>
                          <ToggleButton
                            value={false}
                            aria-label="Vue en liste"
                            sx={{
                              color: (theme) => theme.vars.palette.primary.main,
                            }}
                          >
                            <ViewList />
                          </ToggleButton>
                        </ToggleButtonGroup>
                      </Box>
                      <Divider />
                    </Box>

                    {phaseMode ? (
                      <Box
                        sx={{
                          width: '115vw',
                          transform: 'scale(0.8)',
                          transformOrigin: 'top left',
                          overflowX: 'scroll',
                          scrollBehavior: 'smooth',
                          scrollbarWidth: 'thin',
                          boxSizing: 'content-box',
                        }}
                      >
                        <EliminationCupMode games={phase?.games} />
                      </Box>
                    ) : (
                      <div>
                        {phase.games.map((game, idxGame) => (
                          <>
                            {(rule.rule_type === 3 ||
                              rule.rule_type === 7 ||
                              (rule.rule_type === 6 && phase.rank === 1)) && (
                              <h3
                                css={css`
                                  font-size: 0.875rem;
                                  text-align: center;
                                  margin-bottom: 0.5rem;
                                `}
                              >
                                {game.game.name}
                              </h3>
                            )}
                            <Link
                              key={`${idxGame}`}
                              css={(theme) => css`
                                padding: 0 1rem;
                                border: 1px solid #e2e8ed;
                                color: ${theme.vars.palette.text.primary};
                                background-color: ${theme.vars.palette
                                  .background.paper};
                                transition:
                                  box-shadow 0.5s
                                    cubic-bezier(0.165, 0.84, 0.44, 1),
                                  background-color 0.5s
                                    cubic-bezier(0.165, 0.84, 0.44, 1);
                                margin-top: -1px;
                              `}
                              href={game.game?.url}
                            >
                              <Game key={`${idxGame}`} game={game} />
                              <div
                                css={css`
                                  text-align: center;
                                  font-size: 0.75rem;
                                `}
                              >
                                {[
                                  game.game?.tournament_place?.name,
                                  game.game?.status !== 'to_come' &&
                                    new Date(
                                      game?.game?.date
                                    ).toLocaleDateString([], {
                                      month: 'short',
                                      day: '2-digit',
                                    }),
                                ]
                                  .filter(Boolean)
                                  .join(' - ')}
                              </div>
                            </Link>
                          </>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div>
                    {phase.games.map((game, idxGame) => (
                      <>
                        {(rule.rule_type === 3 ||
                          rule.rule_type === 7 ||
                          (rule.rule_type === 6 && phase.rank === 1)) && (
                          <h3
                            css={css`
                              font-size: 0.875rem;
                              text-align: center;
                              margin-bottom: 0.5rem;
                            `}
                          >
                            {game.game.name}
                          </h3>
                        )}
                        <Link
                          key={`${idxGame}`}
                          css={(theme) => css`
                            padding: 0 1rem;
                            border: 1px solid #e2e8ed;
                            color: ${theme.vars.palette.text.primary};
                            background-color: ${theme.vars.palette.background
                              .paper};
                            transition:
                              box-shadow 0.5s cubic-bezier(0.165, 0.84, 0.44, 1),
                              background-color 0.5s
                                cubic-bezier(0.165, 0.84, 0.44, 1);
                            margin-top: -1px;
                          `}
                          href={game.game?.url}
                        >
                          <Game key={`${idxGame}`} game={game} />
                          <div
                            css={css`
                              text-align: center;
                              font-size: 0.75rem;
                            `}
                          >
                            {[
                              game.game?.tournament_place?.name,
                              game.game?.status !== 'to_come' &&
                                new Date(game?.game?.date).toLocaleDateString(
                                  [],
                                  {
                                    month: 'short',
                                    day: '2-digit',
                                  }
                                ),
                            ]
                              .filter(Boolean)
                              .join(' - ')}
                          </div>
                        </Link>
                      </>
                    ))}
                  </div>
                )}
              </AccordionDetails>
            </Accordion>
          );
        })
      )}

      <Accordion
        square
        sx={{
          width: '100%',
          mt: 2,
          px: 0,
          backgroundColor: 'primary.main',
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMore sx={{ color: 'primary.contrastText' }} />}
          sx={{
            width: '100%',
            fontWeight: 'bold',
            textTransform: 'uppercase',
            color: 'primary.contrastText',
            py: 1,
          }}
        >
          {t(T_KEYS.GENERAL_RANKING)}
        </AccordionSummary>

        <AccordionDetails sx={{ width: '100%' }}>
          <div>
            <div
              css={css`
                display: flex;
                padding: 0.25rem 1rem;
              `}
            >
              <div
                css={css`
                  flex: 1;
                  font-size: 0.75rem;
                `}
              />
            </div>

            {Array(challenge.teams.length)
              .fill(0)
              .map((_, i) => (
                <div
                  key={i}
                  css={css`
                    display: flex;
                    border: 1px solid #e2e8ed;
                    background: white;
                    padding: 0.5rem 1rem;
                    margin-top: -1px;
                    font-size: 0.875rem;
                  `}
                >
                  <div
                    css={css`
                      flex: 0 0 1.5rem;
                      text-align: center;
                    `}
                  >
                    {i + 1}
                  </div>
                  <div
                    css={css`
                      margin-left: 0.25rem;
                      flex: 1;
                    `}
                  >
                    {challenge.teams.find((team) => team.rank === i + 1)
                      ?.name || '-'}
                  </div>
                </div>
              ))}
          </div>
        </AccordionDetails>
      </Accordion>

      <Accordion
        square
        sx={{
          width: '100%',
          mt: 2,
          px: 0,
          backgroundColor: 'primary.main',
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMore sx={{ color: 'primary.contrastText' }} />}
          sx={{
            width: '100%',
            fontWeight: 'bold',
            textTransform: 'uppercase',
            color: 'primary.contrastText',
            py: 1,
          }}
        >
          {t(T_KEYS.BEST_ATTACK_RANKING)}
        </AccordionSummary>
        <AccordionDetails sx={{ width: '100%' }}>
          <div>
            <div
              css={css`
                display: flex;
                padding: 0.25rem 1rem;
              `}
            >
              <div
                css={css`
                  flex: 1;
                  font-size: 0.75rem;
                `}
              />
            </div>

            {[...challenge.teams]
              .sort((a, b) => a.attack_rank - b.attack_rank)
              .map((_, i) => (
                <div
                  key={i}
                  css={css`
                    display: flex;
                    border: 1px solid #e2e8ed;
                    background: white;
                    padding: 0.5rem 1rem;
                    margin-top: -1px;
                    font-size: 0.875rem;
                  `}
                >
                  <div
                    css={css`
                      flex: 0 0 1.5rem;
                      text-align: center;
                    `}
                  >
                    {i + 1}
                  </div>

                  <div
                    css={css`
                      margin-left: 0.25rem;
                      flex: 1;
                    `}
                  >
                    {_.name || '-'}
                  </div>
                  <div
                    css={css`
                      flex: 0 0 1.5rem;
                      text-align: center;
                    `}
                  >
                    {_.attack_points || '-'}
                  </div>
                </div>
              ))}
          </div>
        </AccordionDetails>
      </Accordion>
      <Accordion
        square
        sx={{
          width: '100%',
          mt: 2,
          px: 0,
          backgroundColor: 'primary.main',
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMore sx={{ color: 'primary.contrastText' }} />}
          sx={{
            width: '100%',
            fontWeight: 'bold',
            textTransform: 'uppercase',
            color: 'primary.contrastText',
            py: 1,
          }}
        >
          {t(T_KEYS.BEST_DEFENSE_RANKING)}
        </AccordionSummary>
        <AccordionDetails sx={{ width: '100%' }}>
          <div>
            <div
              css={css`
                display: flex;
                padding: 0.25rem 1rem;
              `}
            >
              <div
                css={css`
                  flex: 1;
                  font-size: 0.75rem;
                `}
              />
            </div>

            {[...challenge.teams]
              .sort((a, b) => a.defence_rank - b.defence_rank)
              .map((_, i) => (
                <div
                  key={i}
                  css={css`
                    display: flex;
                    border: 1px solid #e2e8ed;
                    background: white;
                    padding: 0.5rem 1rem;
                    margin-top: -1px;
                    font-size: 0.875rem;
                  `}
                >
                  <div
                    css={css`
                      flex: 0 0 1.5rem;
                      text-align: center;
                    `}
                  >
                    {i + 1}
                  </div>

                  <div
                    css={css`
                      margin-left: 0.25rem;
                      flex: 1;
                    `}
                  >
                    {_.name || '-'}
                  </div>
                  <div
                    css={css`
                      flex: 0 0 1.5rem;
                      text-align: center;
                    `}
                  >
                    {_.defence_points || '-'}
                  </div>
                </div>
              ))}
          </div>
        </AccordionDetails>
      </Accordion>
    </>
  );
};
